import Box from '@eversports/klimt-primitives/legacy/Box'
import Heading from '@eversports/klimt-primitives/legacy/Heading/Heading'
import Processing from '@eversports/klimt-primitives/legacy/Processing'
import Stack from '@eversports/klimt-primitives/legacy/Stack'
import Text from '@eversports/klimt-primitives/legacy/Text'
import { useSettingsContext } from '@eversports/react-app-base/SettingsContext'

import { From, PrivacySettingField } from '../../../../../App.types'
import MetaHelmet from '../../../../../components/MetaHelmet'
import getStaticPageMetadata from '../../../../../helpers/get-static-page-metadata'
import useGetTopContentHeight from '../../../../../hooks/useGetTopContentHeight'
import { Localized, useIntl } from '../../../../../localization/react'
import PrivateInformation from '../../../components/PrivateInformation'
import PublicUser from '../../../components/PublicUser'
import SectionCard from '../components/SectionCard'

import { PublicUserWithFriends } from './FriendsOfUser.types'
import useFriendsOfUser from './hooks/useFriendsOfUser'
import Navigation from './Navigation'

interface Props {
  user: PublicUserWithFriends
}

const FriendsOfUserComponent = ({ user }: Props) => {
  const intl = useIntl()
  const { origin } = useSettingsContext()
  const { DESKTOP_TOP_CONTENT_HEIGHT } = useGetTopContentHeight(['NAVIGATION_BAR'])
  const { friends, loading, fetchMore, hasMoreResults } = useFriendsOfUser({ initiallyFetchedFriends: user.friends })

  const meta = getStaticPageMetadata({
    title: intl.userFriendsPageMetaTitle({ userFirstName: user.firstName }),
    description: intl.userFriendsPageMetaDescription({ userFirstName: user.firstName }),
    url: `${origin}/friends`,
  })

  return (
    <>
      <MetaHelmet meta={meta} noIndex />
      <Box
        sx={{
          backgroundColor: 'limelite',
          minHeight: `calc(100vh - ${DESKTOP_TOP_CONTENT_HEIGHT}px)`,
          height: '100%',
        }}
        component="main"
      >
        <Stack
          margin="auto"
          maxWidth={{ xs: '100%', md: '600px' }}
          boxSizing="content-box"
          padding={{ xs: 0, md: 6 }}
          gap={{ xs: 0, md: 6 }}
        >
          <Stack gap={{ xs: 0, md: 6 }}>
            <Navigation userFirstName={user.firstName} userHandle={user.handle} />
          </Stack>
          <SectionCard>
            {user.friends ? (
              <>
                <Heading is="h3" color="midnight">
                  <Localized id="friends-of-user-title" params={{ userFirstName: user.firstName }} />
                </Heading>
                {friends.map((friend) => (
                  <PublicUser key={friend.id} user={friend} from={From.USER_FRIENDS_LIST} />
                ))}
                {hasMoreResults && (
                  <Processing isLoading={loading}>
                    <Box
                      sx={{
                        backgroundColor: 'limelite',
                        borderRadius: 1,
                        padding: 2,
                        width: '100%',
                        cursor: 'pointer',
                      }}
                      onClick={fetchMore}
                    >
                      <Text variant="small" sx={{ color: 'primary.main', fontWeight: 'bold', textAlign: 'center' }}>
                        <Localized id="load-more-friends-button" />
                      </Text>
                    </Box>
                  </Processing>
                )}
              </>
            ) : (
              <PrivateInformation field={PrivacySettingField.FRIENDS} />
            )}
          </SectionCard>
        </Stack>
      </Box>
    </>
  )
}

export default FriendsOfUserComponent
